import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import FormularioComissao from "../../Formulario/FormularioComissao";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import TabelaExtratoAdmin from "./TabelaExtratoAdmin";
import Api from "../../Services/Api";

const OMIE_AUTO_SYNC_LAST_DAY_KEY = "omie_auto_sync_last_day";
const BRASILIA_TIME_ZONE = "America/Sao_Paulo";
const DATE_KEY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_PREFIX_REGEX = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/;

function toDateKeyInBrasilia(value = new Date()) {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "string") {
    const clean = value.trim();
    const match = clean.match(DATE_PREFIX_REGEX);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const parts = new Intl.DateTimeFormat("pt-BR", {
    timeZone: BRASILIA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(parsed);

  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  if (!year || !month || !day) return "";
  return `${year}-${month}-${day}`;
}

function toDateOnlyTimestampFromKey(dateKey) {
  const match = String(dateKey || "").match(DATE_KEY_REGEX);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  return new Date(year, month, day).getTime();
}

function shiftDateKey(dateKey, diffDays) {
  const match = String(dateKey || "").match(DATE_KEY_REGEX);
  if (!match) return "";

  const dateUtc = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  );
  dateUtc.setUTCDate(dateUtc.getUTCDate() + Number(diffDays || 0));

  const year = dateUtc.getUTCFullYear();
  const month = String(dateUtc.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dateUtc.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getLocalDateKey(date = new Date()) {
  return toDateKeyInBrasilia(date);
}

function getLastAutoSyncDay() {
  try {
    return localStorage.getItem(OMIE_AUTO_SYNC_LAST_DAY_KEY) || "";
  } catch (_err) {
    return "";
  }
}

function saveLastAutoSyncDay(dayKey) {
  try {
    localStorage.setItem(OMIE_AUTO_SYNC_LAST_DAY_KEY, dayKey);
  } catch (_err) {
    // ignora erro de storage (ex.: modo privado/restrito)
  }
}

function shouldRunDailyAutoSync() {
  const todayKey = getLocalDateKey();
  return getLastAutoSyncDay() !== todayKey;
}

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function pickFirstNumber(...values) {
  for (const value of values) {
    const n = toFiniteNumber(value);
    if (n !== null) return n;
  }
  return null;
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
}

function parseJson(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
}

function normalizeEntryStatus(value) {
  const status = String(value || "").trim().toUpperCase();
  if (!status) return "";
  if (status.includes("CANCEL")) return "CANCELED";
  if (["INACTIVE", "INATIVO", "DELETED", "EXCLUIDO", "EXCLUDED", "REMOVED"].includes(status)) {
    return "CANCELED";
  }
  return status;
}

function readPath(obj, path) {
  if (!obj || typeof obj !== "object" || !path) return null;
  const value = String(path)
    .split(".")
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
  return value === undefined ? null : value;
}

function extractRawOrder(rawInput) {
  const parsed = parseJson(rawInput);
  if (!parsed) return null;

  if (parsed.pedido_venda_produto) return parsed.pedido_venda_produto;
  if (parsed.PedidoVendaProduto) return parsed.PedidoVendaProduto;
  return parsed;
}

function resolveClientDisplayName(source) {
  const raw = extractRawOrder(pickFirst(source?.raw_json, source?.source_raw_json));

  return pickFirst(
    source?.client_trade_name,
    source?.client_fantasy_name,
    source?.nome_fantasia,
    source?.fantasy_name,
    source?.cliente_nome_fantasia,
    source?.client_name_fantasia,
    readPath(raw, "cliente.nome_fantasia"),
    readPath(raw, "Cliente.nome_fantasia"),
    readPath(raw, "cliente.nomeFantasia"),
    readPath(raw, "Cliente.nomeFantasia"),
    readPath(raw, "cabecalho.nome_fantasia"),
    readPath(raw, "Cabecalho.nome_fantasia"),
    readPath(raw, "nome_fantasia"),
    source?.client_name,
    source?.cliente,
    readPath(raw, "cliente.razao_social"),
    readPath(raw, "Cliente.razao_social"),
    readPath(raw, "cliente.nome_cliente"),
    readPath(raw, "Cliente.nome_cliente"),
    readPath(raw, "razao_social"),
    readPath(raw, "nome_cliente")
  );
}

function extractOrderValues(rawInput) {
  const raw = extractRawOrder(rawInput);
  if (!raw || typeof raw !== "object") {
    return {
      total_value: null,
      freight_value: null,
      products_value: null,
      discount_value: null,
      products_net_value: null,
    };
  }

  const totals = raw.total_pedido || raw.TotalPedido || raw.totais || {};
  const freightBlock = raw.frete || raw.Frete || {};
  const discountBlock = raw.desconto || raw.Desconto || {};

  const total_value = pickFirstNumber(
    totals.valor_total_pedido,
    raw.valor_total_pedido,
    raw.total,
    totals.valor_total
  );

  let freight_value = pickFirstNumber(
    totals.valor_frete,
    totals.frete,
    totals.total_frete,
    raw.valor_frete,
    raw.total_frete,
    freightBlock.valor_frete,
    freightBlock.frete,
    freightBlock.total_frete,
    readPath(raw, "transporte.valor_frete"),
    readPath(raw, "Transporte.valor_frete")
  );

  const products_value = pickFirstNumber(
    totals.valor_mercadorias,
    totals.total_mercadorias,
    totals.valor_total_mercadorias,
    totals.total_produtos,
    totals.valor_total_produtos,
    totals.subtotal,
    raw.valor_mercadorias,
    raw.total_mercadorias,
    raw.valor_total_mercadorias,
    raw.total_produtos,
    raw.valor_total_produtos,
    raw.subtotal
  );

  let discount_value = pickFirstNumber(
    totals.valor_desconto,
    totals.valor_descontos,
    totals.desconto,
    totals.total_desconto,
    totals.desc_total,
    totals.valor_desc,
    raw.valor_desconto,
    raw.valor_descontos,
    raw.total_desconto,
    raw.desc_total,
    raw.valor_desc,
    discountBlock.valor_desconto,
    discountBlock.valor_descontos,
    discountBlock.desconto,
    discountBlock.valor
  );

  if (discount_value === null && total_value !== null && products_value !== null && freight_value !== null) {
    const inferredDiscount = Number((products_value + freight_value - total_value).toFixed(2));
    if (Number.isFinite(inferredDiscount) && inferredDiscount > 0) {
      discount_value = inferredDiscount;
    }
  }

  let products_net_value = null;
  if (products_value !== null && discount_value !== null) {
    products_net_value = Number((products_value - discount_value).toFixed(2));
  } else if (total_value !== null && freight_value !== null) {
    products_net_value = Number((total_value - freight_value).toFixed(2));
  } else if (products_value !== null) {
    products_net_value = Number(products_value.toFixed(2));
  }

  if (freight_value === null && total_value !== null && products_net_value !== null) {
    freight_value = Number((total_value - products_net_value).toFixed(2));
  }

  if (products_net_value !== null && products_net_value < 0) products_net_value = 0;
  if (freight_value !== null && freight_value < 0) freight_value = 0;
  if (discount_value !== null && discount_value < 0) discount_value = 0;

  return { total_value, freight_value, products_value, discount_value, products_net_value };
}

function toDateOnlyTimestamp(value) {
  const dateKey = toDateKeyInBrasilia(value);
  if (!dateKey) return null;
  return toDateOnlyTimestampFromKey(dateKey);
}

const LancarComissao = () => {
  const [menuClose, setMenuClose] = useState(false);
  const [licenciados, setLicenciados] = useState([]);
  const [extratoDados, setExtratoDados] = useState([]);
  const [mostrarFuturos, setMostrarFuturos] = useState(false);

  const toggleMenu = () => setMenuClose((prev) => !prev);

  const onItemAtualizado = (novo) => {
    setExtratoDados((prev) =>
      prev.map((x) =>
        String(x.id) === String(novo.id) && (!novo.source || x.source === novo.source)
          ? { ...x, ...novo, user_name: novo.user_name ?? x.user_name }
          : x
      )
    );
  };

  // ✅ carrega licenciados
  useEffect(() => {
    Api.get("/users?role=LICENSED")
      .then((res) => setLicenciados(res.data))
      .catch((err) => console.error("Erro ao buscar licenciados", err));
  }, []);

// ✅ função para carregar extrato do banco (OMIE + LEDGER misturado)
const carregarExtrato = useCallback(async (options = {}) => {
  const forceOrderNumber = String(options?.forceOrderNumber || "").trim();
  const shouldForceOrderLookup = forceOrderNumber !== "";

  try {
    const [omieRes, ledgerRes, omieByOrderRes, ledgerByOrderRes] = await Promise.all([
      // O painel administrativo precisa considerar o mesmo historico completo
      // exibido no extrato do licenciado. O limite anterior de 200 ocultava
      // registros antigos; ao reimporta-los eles recebiam um novo ID e voltavam
      // a aparecer entre os 200 mais recentes, dando a impressao de falha de
      // sincronizacao.
      Api.get("/omie/orders?limit=5000"),
      Api.get("/ledger-entries?limit=5000"),
      shouldForceOrderLookup
        ? Api.get("/omie/orders", {
            params: { q: forceOrderNumber, limit: 50 },
          })
        : Promise.resolve({ data: [] }),
      shouldForceOrderLookup
        ? Api.get("/ledger-entries", {
            params: { q: forceOrderNumber, limit: 50 },
          })
        : Promise.resolve({ data: [] }),
    ]);

    const omieRowsCombined = [
      ...(omieRes.data || []),
      ...(omieByOrderRes?.data || []),
    ];
    const omieRowsUnique = Array.from(
      new Map(omieRowsCombined.map((order) => [String(order.id), order])).values()
    );

    const ledgerRowsCombined = [
      ...(ledgerRes.data || []),
      ...(ledgerByOrderRes?.data || []),
    ];
    const ledgerRowsUnique = Array.from(
      new Map(ledgerRowsCombined.map((entry) => [String(entry.id), entry])).values()
    );

    // Filtra pedidos da Omie que ja foram confirmados (para evitar duplicacao)
    // Pedidos confirmados tem status "CONFIRMED" e ledger_entry_id, entao ja aparecem no ledger
    const omieItens = omieRowsUnique
      .filter((o) => {
        // Exclui pedidos confirmados que ja foram convertidos em ledger entries
        return normalizeEntryStatus(o.status) !== "CONFIRMED" && !o.ledger_entry_id;
      })
      .map((o) => {
        const rawValues = extractOrderValues(o.raw_json);
        const totalPedido = toFiniteNumber(pickFirst(rawValues.total_value, o.total_value));
        const valorFrete = toFiniteNumber(pickFirst(rawValues.freight_value, o.freight_value));
        const valorDesconto = toFiniteNumber(
          pickFirst(rawValues.discount_value, o.discount_value)
        );
        const valorProdutos = toFiniteNumber(
          pickFirst(rawValues.products_net_value, o.products_value, rawValues.products_value)
        );

        const valorCompra = (() => {
          if (valorProdutos !== null) return valorProdutos;
          if (totalPedido !== null && valorFrete !== null) {
            return Number((totalPedido - valorFrete).toFixed(2));
          }
          return Number(o.total_value || 0);
        })();

        // Extrai origem_pedido do raw_json ou usa campo direto se existir
        let origemPedido = null;
        if (o.origem_pedido) {
          origemPedido = o.origem_pedido;
        } else if (o.raw_json) {
          try {
            const raw = typeof o.raw_json === "string" ? JSON.parse(o.raw_json) : o.raw_json;
            origemPedido =
              raw?.cabecalho?.origem_pedido ||
              raw?.Cabecalho?.origem_pedido ||
              raw?.origem_pedido ||
              raw?.canal_venda ||
              null;
          } catch (e) {
            console.warn("Erro ao parsear raw_json:", e);
          }
        }

        return {
          id: o.id,
          source: "OMIE",
          uniqueKey: `OMIE_${o.id}`,

          data: o.order_date, // data de compra
          licenciado_id: o.licensed_id,
          cliente: resolveClientDisplayName(o),
          numero: o.order_number,
          valor_compra: Number(Math.max(0, valorCompra).toFixed(2)),
          valor_total_pedido: Number(Math.max(0, totalPedido ?? valorCompra).toFixed(2)),
          valor_frete: Number(Math.max(0, valorFrete ?? 0).toFixed(2)),
          valor_desconto: Number(Math.max(0, valorDesconto ?? 0).toFixed(2)),
          valor_produtos: Number(Math.max(0, valorProdutos ?? valorCompra).toFixed(2)),
          omie_vendor_code: o.omie_vendor_code,
          valor_comissao: o.commission_value,
          percentual: o.commission_percent,
          status: normalizeEntryStatus(o.status),
          observacao: o.observacao,
          origem_pedido: origemPedido,
          raw_json: o.raw_json,
          commission_items_json: o.commission_items_json,
        };
      });

    const ordersByLedgerId = new Map(omieRowsUnique.filter(o => o.ledger_entry_id).map(o => [String(o.ledger_entry_id), o]));
    const ledgerItens = ledgerRowsUnique.map((l) => ({
      id: l.id,
      source: "LEDGER",
      uniqueKey: `LEDGER_${l.id}`,

      data: l.entry_date, // data de compra/lancamento
      licenciado_id: l.user_id,
      cliente: resolveClientDisplayName(l),
      numero: l.order_number,
      descricao: l.description,
      valor_compra: l.purchase_amount,
      valor_comissao: l.amount,
      percentual: l.commission_percent,
      status: normalizeEntryStatus(l.status),
      origin: l.origin,
      omie_receivable_id: l.omie_receivable_id,
      related_omie_order_id: ordersByLedgerId.get(String(l.id))?.id,
      omie_installment_number: l.omie_installment_number,
      omie_receivable_status: l.omie_receivable_status,
      omie_last_synced_at: l.omie_last_synced_at,
      raw_json: l.raw_json || ordersByLedgerId.get(String(l.id))?.raw_json,
      commission_items_json:
        l.commission_items_json ??
        l.commission_items ??
        l.products_json ??
        l.products ??
        l.items_json,
    }));

    const tudo = [...omieItens, ...ledgerItens].sort((a, b) => {
      const aIsSaqueSolicitado =
        a?.source === "LEDGER" &&
        a?.origin === "WITHDRAW" &&
        a?.status === "WITHDRAW_REQUESTED";
      const bIsSaqueSolicitado =
        b?.source === "LEDGER" &&
        b?.origin === "WITHDRAW" &&
        b?.status === "WITHDRAW_REQUESTED";

      if (aIsSaqueSolicitado && !bIsSaqueSolicitado) return -1;
      if (!aIsSaqueSolicitado && bIsSaqueSolicitado) return 1;

      const da = toDateOnlyTimestamp(a?.data) ?? 0;
      const db = toDateOnlyTimestamp(b?.data) ?? 0;
      return db - da; // mais recente primeiro
    });

    setExtratoDados(tudo);
  } catch (err) {
    console.error("Erro ao carregar extrato unificado", err);
  }
}, []);

// ✅ quando salva lançamento manual (ledger), recarrega tudo (pra manter ordem e mistura)
const aNovoLancamentoAdicionado = () => {
  carregarExtrato();
};

function getLastDaysRange(days = 3) {
  const date_to = getLocalDateKey();
  const date_from = shiftDateKey(date_to, -Math.max(0, Number(days || 0)));
  return {
    date_from,
    date_to,
  };
}

const [importandoOmie, setImportandoOmie] = useState(false);
const [numeroPedidoOmie, setNumeroPedidoOmie] = useState("");
const [importandoPedidoOmieNumero, setImportandoPedidoOmieNumero] = useState(false);
const [sincronizandoAutomatico, setSincronizandoAutomatico] = useState(false);
const acaoImportacaoBloqueada =
  importandoOmie || importandoPedidoOmieNumero || sincronizandoAutomatico;
const sincronizandoRef = useRef(false);
const hojeDateOnlyTs = useMemo(() => {
  return toDateOnlyTimestampFromKey(getLocalDateKey());
}, []);
const extratoFiltrado = useMemo(() => {
  if (mostrarFuturos) return extratoDados;
  return extratoDados.filter((item) => {
    const itemDateTs = toDateOnlyTimestamp(item?.data);
    if (itemDateTs === null) return true;
    return itemDateTs <= hojeDateOnlyTs;
  });
}, [extratoDados, mostrarFuturos, hojeDateOnlyTs]);

// ✅ sincronização silenciosa (para polling automático)
const sincronizarOmieSilencioso = useCallback(async () => {
  // Evita sobreposicao usando ref
  if (sincronizandoRef.current) return false;

  sincronizandoRef.current = true;
  setSincronizandoAutomatico(true);
  let syncOk = false;

  try {
    const { date_from, date_to } = getLastDaysRange(3);

    const { data } = await Api.post("/omie/orders/sync", {
      date_from,
      date_to,
      mode: "manual",
    });

    console.log("[SYNC OMIE AUTOMATICO]", {
      inseridos: data?.inserted ?? 0,
      atualizados: data?.updated ?? 0,
      total: data?.total ?? 0,
    });

    syncOk = true;
    // Recarrega o extrato apos sincronizacao
    await carregarExtrato();
  } catch (err) {
    console.error("[SYNC OMIE AUTOMATICO] Erro:", err?.response?.data || err);
    // Nao mostra alert em sincronizacao automatica
  } finally {
    sincronizandoRef.current = false;
    setSincronizandoAutomatico(false);
  }

  return syncOk;
}, [carregarExtrato]);
const importarUltimosPedidosOmie = async () => {
  setImportandoOmie(true);

  try {
    const { date_from, date_to } = getLastDaysRange(3);

    const { data } = await Api.post("/omie/orders/sync", {
      date_from,
      date_to,
      mode: "manual",
    });

    console.log("[SYNC OMIE RESULT]", data);

    alert(
      `Importação concluída ✅\n\nInseridos: ${data?.inserted ?? 0}\nAtualizados: ${data?.updated ?? 0}\nTotal no período: ${data?.total ?? 0}`
    );
  } catch (err) {
    console.error("Erro sync omie:", err);
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      "Erro ao importar pedidos da Omie";
    alert(msg);
  } finally {
    setImportandoOmie(false);
    carregarExtrato(); // ✅ sempre recarrega
  }
};

const importarPedidoOmiePorNumero = async () => {
  const orderNumber = String(numeroPedidoOmie || "").trim();
  if (!orderNumber) {
    alert("Informe o numero do pedido da Omie.");
    return;
  }

  setImportandoPedidoOmieNumero(true);

  try {
    const { data } = await Api.post("/omie/orders/sync", {
      order_number: orderNumber,
      mode: "manual",
    });

    console.log("[SYNC OMIE POR NUMERO RESULT]", data);

    alert(
      `Importacao por numero concluida.\n\nPedido: ${orderNumber}\nInseridos: ${data?.inserted ?? 0}\nAtualizados: ${data?.updated ?? 0}`
    );
    setNumeroPedidoOmie("");
  } catch (err) {
    console.error("Erro ao importar pedido por numero:", err);
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      "Erro ao importar pedido da Omie por numero";
    alert(msg);
  } finally {
    setImportandoPedidoOmieNumero(false);
    await carregarExtrato({ forceOrderNumber: orderNumber });
  }
};

  // carrega extrato ao abrir a tela
  useEffect(() => {
    carregarExtrato();
  }, [carregarExtrato]);

  // sincronizacao automatica: no maximo 1x por dia (com persistencia por navegador)
  useEffect(() => {
    const CHECK_INTERVAL_MS = 60 * 60 * 1000; // checa a cada 1 hora

    const sincronizarSeNecessario = async () => {
      if (!shouldRunDailyAutoSync()) return;

      const ok = await sincronizarOmieSilencioso();
      if (ok) {
        saveLastAutoSyncDay(getLocalDateKey());
      }
    };

    sincronizarSeNecessario();

    const intervalId = setInterval(() => {
      sincronizarSeNecessario();
    }, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [sincronizarOmieSilencioso]);

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} comissaoActive="active" />

      <main className="main-content">
        <Topbar page="Lançar Comissão" onToggleMenu={toggleMenu} />

        <FormularioComissao
          licenciados={licenciados}
          lancamentoAdicionado={aNovoLancamentoAdicionado}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          {sincronizandoAutomatico && (
            <span
              style={{
                fontSize: 12,
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                className="spinner"
                style={{
                  width: 12,
                  height: 12,
                  border: "2px solid #e0e0e0",
                  borderTop: "2px solid #4CAF50",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              Sincronizando automaticamente...
            </span>
          )}
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "#374151",
              userSelect: "none",
            }}
          >
            <button
              type="button"
              role="switch"
              aria-checked={mostrarFuturos}
              aria-label="Visualizar lancamentos futuros"
              onClick={() => setMostrarFuturos((prev) => !prev)}
              style={{
                width: 42,
                height: 24,
                border: "none",
                borderRadius: 999,
                padding: 2,
                display: "inline-flex",
                alignItems: "center",
                background: mostrarFuturos ? "#16a34a" : "#9ca3af",
                cursor: "pointer",
                transition: "background 0.2s ease",
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: "#fff",
                  transform: mostrarFuturos ? "translateX(18px)" : "translateX(0)",
                  transition: "transform 0.2s ease",
                }}
              />
            </button>
            Visualizar futuros
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="text"
              value={numeroPedidoOmie}
              placeholder="Numero pedido Omie"
              onChange={(event) => setNumeroPedidoOmie(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (!acaoImportacaoBloqueada) {
                    importarPedidoOmiePorNumero();
                  }
                }
              }}
              disabled={acaoImportacaoBloqueada}
              style={{
                height: 36,
                minWidth: 180,
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid #d1d5db",
                fontSize: 14,
              }}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={importarPedidoOmiePorNumero}
              disabled={acaoImportacaoBloqueada}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                whiteSpace: "nowrap",
                opacity: acaoImportacaoBloqueada ? 0.7 : 1,
                cursor: acaoImportacaoBloqueada ? "not-allowed" : "pointer",
              }}
            >
              {importandoPedidoOmieNumero ? (
                <>
                  <span
                    className="spinner"
                    style={{
                      width: 16,
                      height: 16,
                      border: "2px solid #ccc",
                      borderTop: "2px solid #555",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  Importando pedido...
                </>
              ) : (
                <>Importar por numero</>
              )}
            </button>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={importarUltimosPedidosOmie}
            disabled={acaoImportacaoBloqueada}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              opacity: acaoImportacaoBloqueada ? 0.7 : 1,
              cursor: acaoImportacaoBloqueada ? "not-allowed" : "pointer",
            }}
          >
            {importandoOmie ? (
              <>
                <span
                  className="spinner"
                  style={{
                    width: 16,
                    height: 16,
                    border: "2px solid #ccc",
                    borderTop: "2px solid #555",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                Importando pedidos…
              </>
            ) : (
              <>🔄 Importar pedidos da Omie (últimos 3 dias)</>
            )}
          </button>
        </div>



        <TabelaExtratoAdmin
          itensLista={extratoFiltrado}
          licenciados={licenciados}
          onItemAtualizado={onItemAtualizado}
          onReload={carregarExtrato}
        >
          Extrato Admin
        </TabelaExtratoAdmin>

      </main>
    </div>
  );
};

export default LancarComissao;
